/* eslint-disable import/no-extraneous-dependencies */
import {useState, useRef, useEffect} from 'react';
import {throttle} from 'lodash';

const THROTTLE_TIME = 10000; // 10 seconds
const SESSION_CHECK_INTERVAL = 30 * 1000; // 30 seconds
const TOKEN_EXPIRE_THRESHOLD = 7 * 60 * 1000; // TimeLeft to expire session in milliseconds

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'];

export const useTimeout = (
  timerParams: {
    countDownTime: number;
    inActiveTime?: number;
    isAuthV2?: boolean;
    loggedOut?: boolean;
    expireIn?: number;
    tokenExpireThreshold?: number;
    sessionCheckInterval?: number;
  },
  timeOut: () => void,
  refreshSession?: () => void
) => {
  const {countDownTime, inActiveTime, isAuthV2, loggedOut, expireIn, tokenExpireThreshold, sessionCheckInterval} =
    timerParams;
  const [seconds, setSeconds] = useState(countDownTime * 60);
  const [hasSystemError, setHasSystemError] = useState(false);
  const isIdleTimeRef = useRef<boolean>(false);
  const activityTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const sessionTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const countDownTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleIdleTimeout = () => {
    isIdleTimeRef.current = true;
    setHasSystemError(true);
    setSeconds(() => countDownTime * 60);
  };

  useEffect(() => {
    if (countDownTimerRef.current) {
      clearInterval(countDownTimerRef.current);
    }
    if (!(isAuthV2 ? loggedOut : false) && isIdleTimeRef.current && seconds >= 0) {
      countDownTimerRef.current = setInterval(() => {
        if (seconds - 1 >= 0) {
          setSeconds((v) => v - 1);
        }
        if (seconds - 1 < 0) {
          setHasSystemError(false);
          timeOut();
          if (countDownTimerRef.current) {
            clearInterval(countDownTimerRef.current);
          }
        }
      }, 1000);
    }
    return () => {
      if (countDownTimerRef.current) {
        clearInterval(countDownTimerRef.current);
      }
    };
  }, [hasSystemError, seconds]);

  const resetTimer = throttle(() => {
    if (isIdleTimeRef.current) {
      return;
    }
    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }
    activityTimerRef.current = setTimeout(
      () => {
        handleIdleTimeout();
      },
      (inActiveTime ?? 0) * 60 * 1000
    );
  }, THROTTLE_TIME);

  useEffect(() => {
    if (isAuthV2 && !loggedOut) {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
      sessionTimerRef.current = setInterval(() => {
        if (
          expireIn != null &&
          expireIn - Date.now() < (tokenExpireThreshold ?? TOKEN_EXPIRE_THRESHOLD) &&
          refreshSession &&
          !isIdleTimeRef.current
        ) {
          refreshSession();
        }
      }, sessionCheckInterval ?? SESSION_CHECK_INTERVAL);
    } else if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
    }
    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, [isAuthV2, loggedOut, expireIn]);

  const removeActivityEvents = () => {
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, () => {
        resetTimer();
      });
    });
  };

  useEffect(() => {
    setSeconds(() => countDownTime * 60);
    resetTimer();
    removeActivityEvents();
    return () => {
      removeActivityEvents();
    };
  }, []);

  return {
    seconds,
    isIdleTimeRef,
    hasSystemError,
    setHasSystemError
  };
};
